import { Dialog } from '@/modules/dialog'
import { PolarityEmoji } from '@/pages/Mentions/components/Polarity/Polarity.component'
import { BrandRules, BrandTerms, DataSourceType } from '../../types'
import { getCurrentRules, getPolarityIcon } from '../../utils/index'
import { CompoundTermsPreview } from '../CompoundTermsPreview/CompoundTermsPreview.component'
import { RulesPreview } from '../RulesPreview/RulesPreview.component'
import { DialogTitle, DialogTitleDivider, DialogTitleGroup } from './PreviewDialogs.styles'

interface PreviewDialogsProps {
  rules: BrandRules
  termsState: BrandTerms
  activeTab: DataSourceType
}

export function usePreviewDialogs({ rules, termsState, activeTab }: PreviewDialogsProps) {
  const positiveDialog = Dialog.Hook({
    title: (
      <DialogTitle>
        <PolarityEmoji value={getPolarityIcon('POSITIVO')} />
        <span>POSITIVO</span>
      </DialogTitle>
    ),
    content: (
      <RulesPreview
        specificRulesText={getCurrentRules(rules, activeTab).positiveSpecific}
        polarityType="POSITIVO"
        termsState={termsState}
        activeTab={activeTab}
      />
    ),
    size: 'large',
  })

  const negativeDialog = Dialog.Hook({
    title: (
      <DialogTitle>
        <PolarityEmoji value={getPolarityIcon('NEGATIVO')} />
        <span>NEGATIVO</span>
      </DialogTitle>
    ),
    content: (
      <RulesPreview
        specificRulesText={getCurrentRules(rules, activeTab).negativeSpecific}
        polarityType="NEGATIVO"
        termsState={termsState}
        activeTab={activeTab}
      />
    ),
    size: 'large',
  })

  const favorableDialog = Dialog.Hook({
    title: (
      <DialogTitle>
        <PolarityEmoji value={getPolarityIcon('FAVORÁVEL')} />
        <span>FAVORÁVEL</span>
      </DialogTitle>
    ),
    content: (
      <RulesPreview
        specificRulesText={getCurrentRules(rules, activeTab).favorableSpecific}
        polarityType="FAVORÁVEL"
        termsState={termsState}
        activeTab={activeTab}
      />
    ),
    size: 'large',
  })

  const unfavorableDialog = Dialog.Hook({
    title: (
      <DialogTitle>
        <PolarityEmoji value={getPolarityIcon('DESFAVORÁVEL')} />
        <span>DESFAVORÁVEL</span>
      </DialogTitle>
    ),
    content: (
      <RulesPreview
        specificRulesText={getCurrentRules(rules, activeTab).unfavorableSpecific}
        polarityType="DESFAVORÁVEL"
        termsState={termsState}
        activeTab={activeTab}
      />
    ),
    size: 'large',
  })

  const positiveFavorableDialog = Dialog.Hook({
    title: (
      <DialogTitle>
        <DialogTitleGroup>
          <PolarityEmoji value={getPolarityIcon('POSITIVO')} />
          <span>POSITIVO</span>
        </DialogTitleGroup>
        <DialogTitleDivider>/</DialogTitleDivider>
        <DialogTitleGroup>
          <PolarityEmoji value={getPolarityIcon('FAVORÁVEL')} />
          <span>FAVORÁVEL</span>
        </DialogTitleGroup>
      </DialogTitle>
    ),
    content: <CompoundTermsPreview polarity="POSITIVO_FAVORAVEL" termsState={termsState} activeTab={activeTab} />,
    size: 'large',
  })

  const negativeUnfavorableDialog = Dialog.Hook({
    title: (
      <DialogTitle>
        <DialogTitleGroup>
          <PolarityEmoji value={getPolarityIcon('NEGATIVO')} />
          <span>NEGATIVO</span>
        </DialogTitleGroup>
        <DialogTitleDivider>/</DialogTitleDivider>
        <DialogTitleGroup>
          <PolarityEmoji value={getPolarityIcon('DESFAVORÁVEL')} />
          <span>DESFAVORÁVEL</span>
        </DialogTitleGroup>
      </DialogTitle>
    ),
    content: <CompoundTermsPreview polarity="NEGATIVO_DESFAVORAVEL" termsState={termsState} activeTab={activeTab} />,
    size: 'large',
  })

  const emptyTermsState: BrandTerms = {
    socialPositivePublishersTags: [],
    socialNegativePublishersTags: [],
    socialFavorablePublishersTags: [],
    socialUnfavorablePublishersTags: [],
    socialPositiveProprietaryTags: [],
    socialNegativeProprietaryTags: [],
    socialFavorableProprietaryTags: [],
    socialUnfavorableProprietaryTags: [],
    pressPositiveChannelsTags: [],
    pressNegativeChannelsTags: [],
    pressFavorableChannelsTags: [],
    pressUnfavorableChannelsTags: [],
    pressPositiveProprietaryTags: [],
    pressNegativeProprietaryTags: [],
    pressFavorableProprietaryTags: [],
    pressUnfavorableProprietaryTags: [],
    socialPositiveFavorablePublishersTags: [],
    socialPositiveFavorableProprietaryTags: [],
    socialNegativeUnfavorablePublishersTags: [],
    socialNegativeUnfavorableProprietaryTags: [],
    pressPositiveFavorableChannelsTags: [],
    pressPositiveFavorableProprietaryTags: [],
    pressNegativeUnfavorableChannelsTags: [],
    pressNegativeUnfavorableProprietaryTags: [],
  }

  const neutralDialog = Dialog.Hook({
    title: (
      <DialogTitle>
        <PolarityEmoji value={getPolarityIcon('NEUTRO')} />
        <span>NEUTRO</span>
      </DialogTitle>
    ),
    content: (
      <RulesPreview
        specificRulesText={getCurrentRules(rules, activeTab).neutralRules}
        polarityType="NEUTRO"
        termsState={emptyTermsState}
        activeTab={activeTab}
        hideTerms
      />
    ),
    size: 'large',
  })

  function getDialogForPolarity(title: string) {
    switch (title) {
      case 'POSITIVO':
        return positiveDialog
      case 'NEGATIVO':
        return negativeDialog
      case 'FAVORÁVEL':
        return favorableDialog
      case 'DESFAVORÁVEL':
        return unfavorableDialog
      case 'POSITIVO / FAVORÁVEL':
        return positiveFavorableDialog
      case 'NEGATIVO / DESFAVORÁVEL':
        return negativeUnfavorableDialog
      case 'NEUTRO':
        return neutralDialog
      default:
        return positiveDialog
    }
  }

  return {
    positiveDialog,
    negativeDialog,
    favorableDialog,
    unfavorableDialog,
    positiveFavorableDialog,
    negativeUnfavorableDialog,
    neutralDialog,
    getDialogForPolarity,
  }
}
